/**
 * Join wizard frame.
 *
 * The wizard is route based, so each step is linkable and refreshable, and the
 * answers live in the per-token draft store. This file owns the draft access
 * hook, the step order and the two seeds:
 *
 * - an existing participation is loaded from the server, so "join" doubles as
 *   "edit my details" and the user never retypes what the server already knows;
 * - otherwise the nickname starts from the account, which is the only field the
 *   server can fill in for a first-time participant.
 *
 * Joining needs a session, so an anonymous visitor is sent to the sign-in page
 * with this step as the return path.
 */

import { useCallback, useEffect, useRef } from 'react'
import { Outlet, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'

import { AuthGate, useAuthGuard } from '../../../components/layout/guards'
import { PageHeader } from '../../../components/layout/page-header'
import { WizardSteps, type WizardStep } from '../../../components/layout/wizard'
import { Alert, AlertDescription } from '../../../components/ui/alert'
import { useCurrentUser } from '../../../hooks/use-auth'
import { useMyParticipant } from '../../../hooks/use-shared'
import {
  emptyJoinDraft,
  joinDraftFromParticipant,
  useJoinDraftStore,
  type JoinDraft,
} from '../../../stores/drafts'
import { useSessionStore } from '../../../stores/session'
import { useSharedOutlet } from '../shared-layout'

/** Step order of the wizard; the index route redirects to the first entry. */
export const JOIN_STEPS = [
  'location',
  'modes',
  'time',
  'activities',
  'preferences',
  'confirm',
] as const

export type JoinStep = (typeof JOIN_STEPS)[number]

/** The draft used before anything has been stored for this share token. */
const FALLBACK_DRAFT: JoinDraft = emptyJoinDraft()

export interface JoinDraftHandle {
  draft: JoinDraft
  update: (patch: Partial<JoinDraft>) => void
  reset: () => void
}

/**
 * Reads and writes the draft for one share token.
 *
 * The store's `get` would build a fresh empty draft on every call, which a
 * selector must never return, so the stored entry is selected directly and the
 * fallback is a module constant.
 */
export function useJoinDraft(shareToken: string): JoinDraftHandle {
  const userId = useSessionStore((state) => state.user?.id ?? null)
  const stored = useJoinDraftStore((state) => state.entries[shareToken])
  const write = useJoinDraftStore((state) => state.update)
  const resetDraft = useJoinDraftStore((state) => state.reset)

  // A draft belongs to the account that wrote it. The storage is the browser, so
  // another member signing in here must not inherit the previous one's answers - and
  // least of all their departure point.
  const owned =
    stored !== undefined && userId !== null && stored.ownerId === userId ? stored.draft : null

  const update = useCallback(
    (patch: Partial<JoinDraft>) => {
      if (userId !== null) {
        write(shareToken, userId, patch)
      }
    },
    [shareToken, userId, write],
  )
  const reset = useCallback(() => resetDraft(shareToken), [shareToken, resetDraft])

  return {
    draft: owned ?? FALLBACK_DRAFT,
    update,
    reset,
  }
}

/** Absolute path of one wizard step. */
export function joinStepPath(shareToken: string, step: JoinStep): string {
  return `/e/${shareToken}/join/${step}`
}

/** Translation key of one wizard step label, for example `join.stepModes`. */
export function joinStepLabelKey(step: JoinStep): string {
  return `join.step${step.charAt(0).toUpperCase()}${step.slice(1)}`
}

/** Previous and next step of the wizard, when they exist. */
export function joinStepNeighbours(step: JoinStep): {
  previous: JoinStep | undefined
  next: JoinStep | undefined
} {
  const index = JOIN_STEPS.indexOf(step)
  return { previous: JOIN_STEPS[index - 1], next: JOIN_STEPS[index + 1] }
}

export function JoinLayout() {
  const { t } = useTranslation()
  const shared = useSharedOutlet()
  const { shareToken } = shared
  const location = useLocation()
  const { user } = useCurrentUser()
  const guard = useAuthGuard()
  const participantQuery = useMyParticipant(shareToken)
  const { draft, update } = useJoinDraft(shareToken)
  const seededToken = useRef<string | null>(null)

  useEffect(() => {
    if (seededToken.current !== shareToken) {
      if (participantQuery.isPending) {
        return
      }
      if (participantQuery.data) {
        update(joinDraftFromParticipant(participantQuery.data))
        seededToken.current = shareToken
        return
      }
      seededToken.current = shareToken
    }

    // The account nickname is only a starting point: once the user has typed
    // one, or the server participant supplied one, this stops firing.
    if (!draft.nickname && user?.nickname) {
      update({ nickname: user.nickname })
    }
  }, [shareToken, participantQuery.isPending, participantQuery.data, user, draft.nickname, update])

  // The shared gate keeps this page on the same waiting, failure and sign-in
  // redirect branches as every other session-only route.
  if (guard.status !== 'allowed') {
    return <AuthGate guard={guard} />
  }

  const steps: WizardStep[] = JOIN_STEPS.map((step) => ({
    key: step,
    label: t(joinStepLabelKey(step)),
    to: joinStepPath(shareToken, step),
  }))
  const segment = location.pathname.split('/').pop()
  const current: JoinStep = JOIN_STEPS.find((step) => step === segment) ?? JOIN_STEPS[0]

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        breadcrumbs={[
          { label: t('nav.home'), to: '/' },
          { label: shared.event.title, to: `/e/${shareToken}` },
          { label: t('join.title'), to: joinStepPath(shareToken, JOIN_STEPS[0]) },
          { label: t(joinStepLabelKey(current)) },
        ]}
        title={participantQuery.data ? t('join.update') : t('join.title')}
      />

      {participantQuery.data ? (
        <Alert variant="info">
          <AlertDescription>{t('join.joinedBody')}</AlertDescription>
        </Alert>
      ) : null}

      <WizardSteps steps={steps} current={current} />

      <Outlet context={shared} />
    </div>
  )
}
